import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, of, tap, throwError } from 'rxjs';
import { CarritoService } from './carrito.service';
import { AgregarItemCarritoRequest, Carrito, FusionResult, ItemAFusionar, MetodoEntrega } from '../models/carrito.model';

/**
 * Estado del carrito del comprador en señales, sobre {@link CarritoService}.
 *
 * Existe porque el carrito se muestra en tres lugares a la vez —el badge del header, el mini-carrito
 * y la página del carrito— y antes cada uno consultaba por su cuenta: el badge contaba el carrito
 * local de invitado mientras la página leía el del servidor, así que se contradecían. Acá los tres
 * leen la misma señal, de modo que el badge no puede desincronizarse.
 *
 * Cada escritura devuelve el carrito ya actualizado por el backend (que recalcula envío, IGV,
 * comisión y agrupación por vendedor) y la señal se setea con esa respuesta. No se recalcula nada del
 * lado del cliente: el total que se muestra es el que el servidor va a cobrar.
 *
 * Los métodos devuelven el Observable sin tragarse errores, salvo el 404 de "sin carrito" en
 * {@link refrescar}. El manejo de errores queda en el componente, que es el que tiene el contexto de
 * i18n para traducirlos con `mapError`.
 */
@Injectable({ providedIn: 'root' })
export class CartStoreService {
  private readonly carritoService = inject(CarritoService);

  private readonly _carrito = signal<Carrito | null>(null);
  private readonly _loading = signal(false);

  readonly carrito = this._carrito.asReadonly();
  readonly loading = this._loading.asReadonly();

  /** Unidades totales, no líneas distintas: es lo que espera ver el comprador en el badge. */
  readonly itemCount = computed(() => this._carrito()?.itemCount ?? 0);

  readonly estaVacio = computed(() => {
    const carrito = this._carrito();
    return !carrito || carrito.itemCount === 0 || !carrito.grupos?.length;
  });

  /**
   * Tienda a la que pertenece el carrito. `null` es el marketplace abierto y la tienda pública.
   *
   * Importa porque en base de datos hay un carrito activo por (comprador, tienda)
   * —`uq_cart_active_per_user`— así que pedir con un `storefrontId` distinto direcciona otro carrito.
   * Una vista de canal (B2B2C) debe fijarlo antes de leer, o verá el carrito equivocado.
   */
  private readonly _storefrontId = signal<number | null>(null);
  readonly storefrontId = this._storefrontId.asReadonly();

  fijarStorefront(storefrontId: number | null): void {
    this._storefrontId.set(storefrontId);
  }

  /**
   * Relee el carrito activo. Un 404 no es un error: es el estado normal de quien todavía no agregó
   * nada, y se traduce a `null` (carrito vacío) sin molestar al comprador con un aviso.
   */
  refrescar(context?: HttpContext): Observable<Carrito | null> {
    this._loading.set(true);
    return this.carritoService.obtenerActivo(this._storefrontId(), context).pipe(
      tap(carrito => {
        this._carrito.set(carrito);
        this._loading.set(false);
      }),
      catchError((err: HttpErrorResponse) => {
        this._loading.set(false);
        if (err.status === 404) {
          this._carrito.set(null);
          return of(null);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Cuenta de altas exitosas. La tienda la observa para abrir el mini-carrito tras agregar: es la
   * confirmación más barata de que el producto entró, y no depende de que quien agrega (catálogo,
   * ficha, home) conozca al mini-carrito, que vive en el layout.
   */
  private readonly _altas = signal(0);
  readonly altas = this._altas.asReadonly();

  agregar(sellerOfferId: number, quantity: number, context?: HttpContext): Observable<Carrito> {
    const request: AgregarItemCarritoRequest = {
      sellerOfferId,
      quantity,
      storefrontId: this._storefrontId()
    };
    return this.carritoService.agregarItem(request, context).pipe(
      this.sincronizar(),
      tap(() => this._altas.update(n => n + 1))
    );
  }

  cambiarCantidad(itemId: number, quantity: number, context?: HttpContext): Observable<Carrito> {
    return this.carritoService
      .modificarCantidad(itemId, { quantity, storefrontId: this._storefrontId() }, context)
      .pipe(this.sincronizar());
  }

  quitar(itemId: number, context?: HttpContext): Observable<Carrito> {
    return this.carritoService.eliminarItem(itemId, this._storefrontId(), context).pipe(this.sincronizar());
  }

  /** M02/01 RF-010: acepta el precio vigente de un ítem cuyo precio cambió, para poder confirmar. */
  aceptarPrecio(itemId: number, context?: HttpContext): Observable<Carrito> {
    return this.carritoService.aceptarPrecio(itemId, this._storefrontId(), context).pipe(this.sincronizar());
  }

  /** M02/01 RF-014: vacía el carrito. Queda vacío pero abierto. */
  vaciar(context?: HttpContext): Observable<Carrito> {
    return this.carritoService.vaciar(this._storefrontId(), context).pipe(this.sincronizar());
  }

  /** Cambia el método de entrega de un vendedor; la respuesta trae el carrito con envío, IGV y totales recalculados. */
  cambiarMetodoEntrega(sellerClientId: number, deliveryMethod: MetodoEntrega, context?: HttpContext): Observable<Carrito> {
    return this.carritoService
      .cambiarMetodoEntrega(sellerClientId, deliveryMethod, this._storefrontId(), context)
      .pipe(this.sincronizar());
  }

  guardarParaDespues(itemId: number, context?: HttpContext): Observable<Carrito> {
    return this.carritoService.guardarParaDespues(itemId, this._storefrontId(), context).pipe(this.sincronizar());
  }

  moverAlCarrito(savedItemId: number, context?: HttpContext): Observable<Carrito> {
    return this.carritoService.moverAlCarrito(savedItemId, this._storefrontId(), context).pipe(this.sincronizar());
  }

  /** M02.2.1 RF-022: recupera un carrito abandonado y lo vuelve a dejar como el carrito activo. */
  reactivar(cartId: number, context?: HttpContext): Observable<Carrito> {
    return this.carritoService.reactivar(cartId, context).pipe(this.sincronizar());
  }

  /**
   * M02/01 RF-026: vuelca un lote de ítems (carrito de invitado heredado de localStorage) al carrito
   * activo. La adopción normal ya la hace el backend al iniciar sesión; esto queda para migrar lo que
   * haya quedado guardado en el navegador.
   */
  fusionar(items: ItemAFusionar[], context?: HttpContext): Observable<FusionResult> {
    return this.carritoService.fusionar(items, this._storefrontId(), context).pipe(
      tap(resultado => {
        if (resultado.cart) {
          this._carrito.set(resultado.cart);
        }
      })
    );
  }

  /** Limpia el estado local sin tocar el servidor (p. ej. al cerrar sesión). */
  limpiar(): void {
    this._carrito.set(null);
    this._loading.set(false);
  }

  private sincronizar() {
    return (fuente: Observable<Carrito>): Observable<Carrito> =>
      fuente.pipe(tap(carrito => this._carrito.set(carrito)));
  }
}
